import { Platform } from 'react-native';
import { withEntryAttempt } from './entryAttempt';
import { storage } from '../utils/storage';
import { API_BASE_URL as BASE_URL } from './apiUrl';
import { DEV_RU_EDGE_ORIGIN, DEV_DIRECT_API_ORIGIN } from '../config/ruDevEdge';

/**
 * CORE-604 — engineering telemetry for the FE↔API network layer, focused on the
 * RU-first host routing. Answers «почему у юзера в РФ (под VPN) не грузится лента
 * мастеров»: which host was chosen (direct vs api-ru) and why, whether the RU
 * edge was reachable, whether the gateway-5xx fallback fired, and which home/live
 * blocks gave up loading. The BE stamps the GeoIP country (the VPN-exit slice) and
 * a daily Telegram report summarizes it.
 *
 * Mirrors src/services/uploadTelemetry.ts for reliability: a persisted queue
 * (localStorage on web), a 5s flush, beacon on tab-hide, and client-idempotent
 * event ids the BE dedupes. Emitters only enqueue — the timer does the POST — so
 * a decision made *inside* the fetch patch never re-enters it synchronously.
 */

const STORAGE_KEY = 'agape_net_telemetry_queue_v1';
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 50;
const MAX_QUEUE_SIZE = 300;
const ENDPOINT = '/api/telemetry/network-events';
const SEND_TIMEOUT_MS = 15000;

const PRIMARY_HOST = 'https://api.agapella.io';
const RU_HOST = 'https://api-ru.agapella.io';

/**
 * CORE-604 — dual-host delivery for reliability. Network telemetry is *about* the
 * network layer, so it must survive the very block it's reporting: if the RU ISP
 * shapes the direct host, the events describing that block can't be sent through
 * it. On the prod origin we POST every batch to BOTH the direct host AND the RU
 * edge (which proxies the same DB); whichever is reachable delivers, and the
 * per-event ClientEventId + BE `ON CONFLICT DO NOTHING` means a batch that reaches
 * both is stored exactly once. On dev / other builds there's no RU edge, so we
 * single-send to the configured base — the old path, unchanged.
 */
function deliveryTargets(): string[] {
  try {
    const host = new URL(BASE_URL).host;
    if (/(^|\.)api\.agapella\.io$/.test(host)) return [PRIMARY_HOST, RU_HOST];
    // CORE-2115 — the same reasoning on the dev edge: when api.rudev is cut for
    // this visitor, the events describing the fallback to api.agdev would die on
    // api.rudev too. Both hosts reach the same dev DB; ClientEventId dedupes.
    if (`https://${host}` === DEV_RU_EDGE_ORIGIN) return [DEV_RU_EDGE_ORIGIN, DEV_DIRECT_API_ORIGIN];
  } catch { /* fall through */ }
  return [BASE_URL.replace(/\/+$/, '')];
}

/**
 * The global fetch captured at module load — BEFORE App.tsx installs the RU-first
 * rewrite / api-ru fallback patches at runtime — so telemetry controls its own
 * host targeting and isn't re-routed by the layer it measures. Both patches only
 * ever rewrite the api.agapella.io host, so even if this ever captured a patched
 * fetch the RU target still lands (direct would just fold onto RU — deduped).
 */
const rawFetch: typeof fetch = (() => {
  const g: any = (typeof window !== 'undefined' ? window : globalThis);
  return typeof g.fetch === 'function' ? g.fetch.bind(g) : fetch;
})();

interface PersistedEvent {
  clientEventId: string;
  eventName: string;
  host: string | null;
  ruActive: boolean | null;
  ruSignal: boolean | null;
  decisionSource: string | null;
  probeOutcome: string | null;
  surface: string | null;
  errorKind: string | null;
  httpStatus: number | null;
  attemptCount: number | null;
  pageUrl: string | null;
  timestampUtc: string;
  properties: Record<string, any>;
}

function isWeb(): boolean {
  return Platform.OS === 'web' && typeof window !== 'undefined';
}

function isEnabled(): boolean {
  return Platform.OS === 'web' ? typeof window !== 'undefined' : true;
}

function genId(): string {
  if (isWeb() && typeof crypto !== 'undefined' && (crypto as any).randomUUID) {
    return (crypto as any).randomUUID();
  }
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let s = '';
  for (let i = 0; i < 24; i++) s += chars[Math.floor(Math.random() * chars.length)];
  return `${Date.now().toString(36)}-${s}`;
}

function readPersistedQueue(): PersistedEvent[] {
  if (!isWeb()) return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function writePersistedQueue(queue: PersistedEvent[]): void {
  if (!isWeb()) return;
  try {
    const trimmed = queue.length > MAX_QUEUE_SIZE ? queue.slice(queue.length - MAX_QUEUE_SIZE) : queue;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch { /* quota — never crash over telemetry */ }
}

class NetTelemetryClient {
  private queue: PersistedEvent[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private isFlushing = false;

  constructor() {
    if (!isEnabled()) return;
    this.queue = readPersistedQueue();
    this.flushTimer = setInterval(() => { this.flush().catch(() => {}); }, FLUSH_INTERVAL_MS);
    // CORE-1582 — under node the module-level client's timer kept the process
    // alive: jest in one process never exited after a full run (six copies from
    // the re-imports in apiFallbackTransportRetry-CORE1066). Browsers and RN
    // return a number with no `unref`, so this changes nothing there.
    (this.flushTimer as { unref?: () => void } | null)?.unref?.();
    if (isWeb()) {
      window.addEventListener('beforeunload', () => { this.flushBeacon(); });
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') this.flushBeacon();
      });
    }
    if (this.queue.length > 0) setTimeout(() => { this.flush().catch(() => {}); }, 250);
  }

  push(event: PersistedEvent) {
    if (!isEnabled()) return;
    this.queue.push(event);
    if (this.queue.length > MAX_QUEUE_SIZE) this.queue = this.queue.slice(this.queue.length - MAX_QUEUE_SIZE);
    writePersistedQueue(this.queue);
  }

  private async flush() {
    if (!isEnabled() || this.queue.length === 0 || this.isFlushing) return;
    this.isFlushing = true;
    const batch = this.queue.slice(0, MAX_BATCH_SIZE);
    try {
      const headers: HeadersInit = { 'Content-Type': 'application/json' };
      const token = await storage.getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const body = JSON.stringify({ events: batch });

      // Dual-host: deliver to every target; the ClientEventId dedup makes a
      // both-arrive safe. Delivered if AT LEAST ONE host accepted the batch.
      const results = await Promise.allSettled(
        deliveryTargets().map((base) => this.postOne(base, body, headers)),
      );
      const delivered = results.some((r) => r.status === 'fulfilled' && r.value === true);
      if (delivered) {
        this.queue = this.queue.slice(batch.length);
        writePersistedQueue(this.queue);
      }
      // No host accepted it → keep the batch, retry next tick.
    } catch {
      // Never crash over telemetry — keep the batch queued.
    } finally {
      this.isFlushing = false;
    }
  }

  private async postOne(base: string, body: string, headers: HeadersInit): Promise<boolean> {
    try {
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), SEND_TIMEOUT_MS);
      try {
        const res = await rawFetch(`${base}${ENDPOINT}`, { method: 'POST', headers, body, signal: ctrl.signal });
        return res.ok || res.status === 204 || res.status === 400 || res.status === 413;
      } finally {
        clearTimeout(timer);
      }
    } catch {
      return false; // this host is unreachable — the other target may still deliver
    }
  }

  private flushBeacon() {
    if (!isWeb() || this.queue.length === 0) return;
    if (typeof navigator.sendBeacon !== 'function') return;
    try {
      const batch = this.queue.slice(0, MAX_BATCH_SIZE);
      const blob = new Blob([JSON.stringify({ events: batch })], { type: 'application/json' });
      // Beacon can't read a response, so fire at every target and treat any
      // accepted enqueue as delivered (dedup covers a double-arrive).
      let anyQueued = false;
      for (const base of deliveryTargets()) {
        if (navigator.sendBeacon(`${base}${ENDPOINT}`, blob)) anyQueued = true;
      }
      if (anyQueued) {
        this.queue = this.queue.slice(batch.length);
        writePersistedQueue(this.queue);
      }
    } catch { /* swallow */ }
  }
}

const client = new NetTelemetryClient();

// Client-known identity (mirrors uploadTelemetry) — the BE stamps the JWT UserId,
// but beacon flushes can't carry an Authorization header, so stamp it into props.
let currentUserId: string | null = null;
export function setNetTelemetryUser(userId: string | null): void { currentUserId = userId; }

function pageUrl(): string | null {
  return isWeb() ? window.location.pathname : null;
}

interface NetFields {
  host?: string | null;
  ruActive?: boolean | null;
  ruSignal?: boolean | null;
  decisionSource?: string | null;
  probeOutcome?: string | null;
  surface?: string | null;
  errorKind?: string | null;
  httpStatus?: number | null;
  attemptCount?: number | null;
}

function emit(eventName: string, fields: NetFields, properties: Record<string, any> = {}) {
  if (!isEnabled()) return;
  try {
    client.push({
      clientEventId: genId(),
      eventName,
      host: fields.host ?? null,
      ruActive: fields.ruActive ?? null,
      ruSignal: fields.ruSignal ?? null,
      decisionSource: fields.decisionSource ?? null,
      probeOutcome: fields.probeOutcome ?? null,
      surface: fields.surface ?? null,
      errorKind: fields.errorKind ?? null,
      httpStatus: fields.httpStatus ?? null,
      attemptCount: fields.attemptCount ?? null,
      pageUrl: pageUrl(),
      timestampUtc: new Date().toISOString(),
      // CORE-1884 — the entry attempt the host decision / fallback happened in.
      properties: withEntryAttempt(currentUserId ? { ...properties, userId: currentUserId } : properties),
    });
  } catch { /* never let telemetry crash a request */ }
}

// ── Public API ────────────────────────────────────────────────────────────

/** The RU-first host-routing decision, emitted once per resolution (apiEndpoint). */
export function trackHostDecision(fields: {
  ruActive: boolean; ruSignal: boolean;
  // CORE-2115 — 'fallback': the dev web build repeated a call on api.agdev
  // after api.rudev failed at the network level (apiFallback.ts).
  decisionSource: 'optimistic' | 'probe' | 'cache' | 'fallback';
  probeOutcome?: 'ok' | 'hard_fail' | 'timeout' | null;
  properties?: Record<string, any>;
}) {
  emit('host_decision', {
    host: fields.ruActive ? 'api-ru' : 'direct',
    ruActive: fields.ruActive,
    ruSignal: fields.ruSignal,
    decisionSource: fields.decisionSource,
    probeOutcome: fields.probeOutcome ?? null,
  }, fields.properties ?? {});
}

/**
 * A fallback to api-ru happened (apiFallback). httpStatus = the api-ru status.
 *
 * CORE-1323 — событие эмитится из ТРЁХ мест, разделяемых `properties.phase`;
 * схема (имя события и колонки) та же, что завёл CORE-604, добавились только
 * точки вызова и значения:
 *
 *  - `attempt` — повтор на api-ru НАЧАТ. Пишется до запроса намеренно: отказ,
 *    ради которого всё это, — «висит и не отвечает», и пользователь уходит со
 *    страницы, не дождавшись. Без парной `outcome` строка «попытка была» —
 *    сама по себе диагноз (та же логика, что у `auth_attempt` в CORE-1200).
 *  - `outcome` — повтор ЗАВЕРШИЛСЯ: `httpStatus` — ответ api-ru, `null` —
 *    api-ru тоже не ответил (`ruReached: false`).
 *  - `sticky` — брейкер защёлкнулся, весь дальнейший трафик вкладки идёт на
 *    api-ru; `decisionSource` говорит, от чего именно.
 *
 * `errorKind` теперь несёт не только `gateway_5xx`, но и класс первичного
 * транспортного отказа (`network` — TypeError, `timeout` — TimeoutError).
 */
export function trackApiRuFallback(fields: {
  phase?: 'attempt' | 'outcome' | 'sticky';
  errorKind?: string;
  httpStatus?: number | null;
  primaryStatus?: number | null;
  decisionSource?: string | null;
  method?: string | null;
  path?: string | null;
}) {
  const phase = fields.phase ?? 'outcome';
  emit('api_ru_fallback', {
    host: 'api-ru',
    errorKind: fields.errorKind ?? 'gateway_5xx',
    httpStatus: fields.httpStatus ?? null,
    decisionSource: fields.decisionSource ?? null,
  }, {
    phase,
    ...(fields.primaryStatus != null ? { primaryStatus: fields.primaryStatus } : {}),
    ...(fields.method ? { method: fields.method } : {}),
    ...(fields.path ? { path: fields.path } : {}),
    ...(phase === 'outcome' ? { ruReached: fields.httpStatus != null } : {}),
  });
}

/** Best-effort error-kind label from a thrown fetch/api error. */
function errorKindOf(err: unknown): string {
  const status = (err as any)?.status ?? (err as any)?.response?.status;
  if (typeof status === 'number') {
    if (status === 502 || status === 503 || status === 504) return 'gateway_5xx';
    if (status >= 500) return 'server_5xx';
    if (status >= 400) return 'http_4xx';
  }
  const name = (err as any)?.name;
  if (name === 'TimeoutError' || name === 'AbortError') return 'timeout';
  if (err instanceof TypeError) return 'network';
  if (typeof name === 'string' && name) return name;
  return 'unknown';
}

/**
 * CORE-1200 — попытка авторизации началась.
 *
 * Пишется ДО запроса: если вкладку закроют на висящем fetch, в базе останется
 * «попытка была», а отсутствие парного `auth_outcome` — само по себе диагноз.
 * Поля собирает `authDiagnostics.ts`, здесь только доставка — этот канал
 * (двухадресный, с очередью и beacon'ом) для того и выбран, что он переживает
 * ровно тот сетевой отказ, о котором сообщает.
 */
export function trackAuthAttempt(fields: Record<string, unknown>) {
  emit('auth_attempt', {
    host: fields.apiHost === 'api-ru' ? 'api-ru' : 'direct',
    surface: 'auth',
  }, fields);
}

/** CORE-1200 — исход попытки: успех или отказ с классом и длительностью. */
export function trackAuthOutcome(fields: Record<string, unknown>) {
  const status = fields.httpStatus;
  emit('auth_outcome', {
    host: fields.apiHost === 'api-ru' ? 'api-ru' : 'direct',
    surface: 'auth',
    errorKind: typeof fields.errorKind === 'string' ? fields.errorKind : null,
    httpStatus: typeof status === 'number' ? status : null,
  }, fields);
}

/** A home/live block gave up loading after its retries (the catch path). */
export function trackContentLoadFailed(
  surface: string,
  err: unknown,
  opts?: { attemptCount?: number | null },
) {
  const status = (err as any)?.status ?? (err as any)?.response?.status;
  emit('content_load_failed', {
    surface,
    errorKind: errorKindOf(err),
    httpStatus: typeof status === 'number' ? status : null,
    attemptCount: opts?.attemptCount ?? null,
  }, { message: (err as any)?.message ? String((err as any).message).slice(0, 300) : undefined });
}
